import React, { useMemo, useState } from 'react';
import { Modal, Form, Select, Radio, Input, Alert, Typography } from 'antd';
import { useMsal } from '@azure/msal-react';
import { apiConfig, apiTokenRequest } from '../authConfig';
import { setActiveImpersonation } from '../utils/impersonationFetch';

const { Text } = Typography;
const { TextArea } = Input;

const MIN_REASON_LENGTH = 30;

/** Backend allowlist — .claude/design/impersonation.md "API contract" (POST /impersonation/start). */
const ROLE_OPTIONS = [
    { label: 'Advisor', value: 'advisor' },
    { label: 'Company SPOC', value: 'company_spoc' },
    { label: 'Company Delegate', value: 'company_delegate' },
];

const DURATION_OPTIONS = [
    { label: '30 min', value: 30 },
    { label: '1 hr', value: 60 },
    { label: '2 hrs', value: 120 },
    { label: '4 hrs', value: 240 },
];

/**
 * "Act on behalf of…" dialog — a Lead Advisor starts a time-boxed impersonation session.
 *
 * Design spec: .claude/design/impersonation.md ("UI affordance" > "Start flow") +
 * .claude/design/approval-matrix-clarification.md (R026/R027 — approval-matrix "Act on
 * behalf of" affordance; the pre-fill + role-scoping props below implement that doc's
 * "the dialog pre-fills the reason with the action's name (editable)" requirement).
 *
 * Persona-gating for WHO may open this dialog is the caller's responsibility (LEAD_ADVISOR
 * only) — this component does not re-check the role itself, it trusts its mount condition.
 *
 * `members` is the project's ProjectMember list, passed in by the caller (App.jsx's global
 * Ctrl+Shift+I hotkey, or a scoped caller like LeadAdvisorApprovalMatrixPanel) — this
 * component does not fetch it.
 *
 * @param {object} props
 * @param {string} [props.initialEffectiveRole] — when set, scopes the dialog to this persona
 *   (`advisor` | `company_spoc` | `company_delegate`): pre-selects it, locks the role picker,
 *   and titles the dialog "Act on behalf of {persona}…". Omit for the free-choice flow.
 * @param {string} [props.initialReason] — pre-fills the reason textarea with the blocked
 *   action's name; still freely editable and still subject to the same ≥30-char floor.
 */
export default function ImpersonationDialog({
    open,
    onClose,
    projectId,
    projectName,
    members = [],
    initialEffectiveRole,
    initialReason,
}) {
    const { instance } = useMsal();
    const [form] = Form.useForm();
    const effectiveRole = Form.useWatch('effectiveRole', form);
    const reason = Form.useWatch('reason', form);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const reasonLength = (reason || '').trim().length;
    const reasonValid = reasonLength >= MIN_REASON_LENGTH;
    const scopedRoleOption = ROLE_OPTIONS.find((r) => r.value === initialEffectiveRole) || null;
    const dialogTitle = scopedRoleOption ? `Act on behalf of ${scopedRoleOption.label}…` : 'Act on behalf of…';

    const userOptions = useMemo(() => {
        const selected = ROLE_OPTIONS.find((r) => r.value === effectiveRole);
        if (!selected) return [];
        // `m.role` on a ProjectMember row is the raw lowercase DB/API token (e.g. 'advisor',
        // 'company_spoc') — `selected.value` is that same vocabulary. Never compare against
        // the frontend's uppercase `Role.*` app-role constants here; see lesson
        // [[frontend-role-comparison-casing-and-placeholder-id]] (this was that exact bug: a
        // `Role.ADVISOR` comparison against lowercase membership rows, always false, silently
        // leaving this dropdown permanently empty).
        return (members || [])
            .filter((m) => m.role === selected.value)
            .map((m) => ({
                value: m.id,
                label: m.email ? `${m.name} (${m.email})` : m.name,
            }));
    }, [effectiveRole, members]);

    const handleClose = () => {
        if (submitting) return;
        setError(null);
        form.resetFields();
        onClose?.();
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        setError(null);
        try {
            const account = instance.getActiveAccount();
            const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });

            const trimmedReason = values.reason.trim();
            const body = {
                projectId,
                effectiveRole: values.effectiveRole,
                reason: trimmedReason,
                durationMinutes: values.durationMinutes || 30,
            };
            if (values.effectiveUserId) body.effectiveUserId = values.effectiveUserId;

            const res = await fetch(`${apiConfig.baseUrl}/api/v1/impersonation/start`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${tokenResponse.accessToken}`,
                },
                body: JSON.stringify(body),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) {
                throw new Error(json.error?.message || `Could not start impersonation session (${res.status})`);
            }

            const data = json.data || {};
            const selectedUser = userOptions.find((o) => o.value === values.effectiveUserId);
            const roleLabel = ROLE_OPTIONS.find((r) => r.value === values.effectiveRole)?.label || values.effectiveRole;

            setActiveImpersonation({
                impersonationId: data.impersonationId,
                expiresAt: data.expiresAt,
                effectiveRole: data.effectiveRole || values.effectiveRole,
                effectiveUserId: data.effectiveUserId ?? values.effectiveUserId ?? null,
                projectId: data.projectId || projectId,
                // Display-only extras the API response doesn't carry — captured now, while we
                // still have the project/member context, so the banner never has to re-fetch.
                effectiveUserLabel: selectedUser?.label || `role: ${roleLabel}`,
                projectName,
            });

            // Full reload: impersonationHeaders()/the `impersonated` opt-in is wired only at
            // specific, opted-in call sites so far (AppContext.jsx::saveScopingProfile's
            // `{ impersonated: true }` flag, used today by the org-setup "Act on behalf"
            // completion actions — R026/R027) — most fetch call sites in the app still don't
            // spread it and have no reason to. A hard reload remains the simplest way to make
            // every OTHER already-mounted surface in this tab (persona-gated menus, other
            // wizard panes, etc.) pick up the new session, since a sessionStorage write alone
            // does not re-render components that already read `getActiveImpersonation()` once.
            window.location.reload();
        } catch (err) {
            console.error('Failed to start impersonation session:', err);
            setError(err.message || 'Failed to start impersonation session.');
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            title={dialogTitle}
            okText="Start session"
            okButtonProps={{ disabled: !reasonValid, loading: submitting, danger: true }}
            cancelButtonProps={{ disabled: submitting }}
            onOk={() => form.submit()}
            destroyOnClose
            width={520}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    durationMinutes: 30,
                    effectiveRole: initialEffectiveRole || undefined,
                    reason: initialReason || undefined,
                }}
            >
                <Form.Item
                    name="effectiveRole"
                    label="Impersonate role"
                    rules={[{ required: true, message: 'Select a role to act as' }]}
                    extra={scopedRoleOption ? 'Scoped to the persona blocking this step - see the approval matrix row.' : undefined}
                >
                    <Select
                        placeholder="Select a role"
                        options={ROLE_OPTIONS.map(({ value, label }) => ({ value, label }))}
                        disabled={!!scopedRoleOption}
                        onChange={() => form.setFieldValue('effectiveUserId', undefined)}
                    />
                </Form.Item>

                <Form.Item
                    name="effectiveUserId"
                    label="Specific user (optional - leave blank to act as the role generically)"
                >
                    <Select
                        allowClear
                        placeholder={effectiveRole ? 'Select a project member' : 'Select a role first'}
                        disabled={!effectiveRole}
                        options={userOptions}
                        notFoundContent={effectiveRole ? 'No project members with this role' : undefined}
                    />
                </Form.Item>

                <Form.Item
                    name="reason"
                    label={`Reason (minimum ${MIN_REASON_LENGTH} characters - recorded in the audit log)`}
                    rules={[{ required: true, message: 'A reason is required' }]}
                >
                    <TextArea
                        rows={3}
                        maxLength={1000}
                        showCount
                        placeholder="e.g. Advisor is on leave; SPOC has asked us to submit the risk register on their behalf ahead of Friday's review."
                    />
                </Form.Item>
                {!reasonValid && reasonLength > 0 && (
                    <Text type="danger" style={{ display: 'block', marginTop: -16, marginBottom: 16 }}>
                        {MIN_REASON_LENGTH - reasonLength} more character{MIN_REASON_LENGTH - reasonLength === 1 ? '' : 's'} needed.
                    </Text>
                )}

                <Form.Item name="durationMinutes" label="Duration" rules={[{ required: true }]}>
                    <Radio.Group options={DURATION_OPTIONS} optionType="button" buttonStyle="solid" />
                </Form.Item>

                {error && <Alert type="error" showIcon message={error} style={{ marginTop: 8 }} />}
            </Form>
        </Modal>
    );
}
